export const kitchenStories = {
  id: 'kitchen-stories',
  title: 'Cooking 44% Faster on Kitchen Stories',
  subtitle: 'Fixing the friction points between recipe discovery and the first step of cooking',
  tags: ['Cooking', 'Mobile', 'UX Design', 'UX Research'],
  description: 'Kitchen Stories is a mobile app focused on recipe discovery and guided cooking. A targeted redesign of the browse-to-cook transition reduced task completion time by 44% and brought Start Cooking discovery from 60% to 95%.',
  role: 'End-to-end UX design',
  tools: 'Figma Design',
  platform: 'Mobile / iOS & Android',
  year: '2025',

  sections: [
    {
      type: 'hero-images',
      images: [
        { src: '/images/kitchen-stories/hero.png?v=3', alt: 'Kitchen Stories' }
      ]
    },
    {
      type: 'two-col-text',
      heading: 'The Problem',
      content: [
        'Kitchen Stories helps people find and cook recipes. But between finding a recipe and actually starting to cook it, users kept getting stuck. The Start Cooking button was easy to miss. Instructions were fragmented and hard to follow mid-cook. Ingredients were buried. And premium features appeared without warning, eroding trust at exactly the wrong moment.',
        "The biggest friction wasn't any one screen — it was the cumulative weight of small confusions at the moment of highest intent."
      ]
    },
    {
      type: 'image-grid',
      columns: 4,
      images: [
        { src: '/images/kitchen-stories/problem-1.png', alt: 'Hard-to-find Start Cooking button', caption: 'Hard-to-find Start Cooking button' },
        { src: '/images/kitchen-stories/problem-2.png', alt: 'Fragmented step-by-step instructions', caption: 'Fragmented step-by-step instructions' },
        { src: '/images/kitchen-stories/problem-3.png', alt: 'Utensils and ingredients hard to scan', caption: 'Utensils and ingredients hard to scan' },
        { src: '/images/kitchen-stories/problem-4.png', alt: 'Unlabelled premium features', caption: 'Unlabelled premium features' }
      ]
    },
    {
      type: 'two-col-text',
      heading: 'Research Approach',
      content: [
        'I ran two rounds of moderated usability testing, anchored around three realistic tasks: find a 30-minute recipe, start the cooking flow, and save it for later. Round 1 identified where users hesitated and why. Round 2 validated whether the design changes actually fixed the problems — or just moved them.'
      ]
    },
    {
      type: 'callout',
      text: 'Key focus areas: entry point clarity, in-context ingredient access, premium feature transparency, and save-flow friction.'
    },
    {
      type: 'two-col-text',
      heading: 'Design Decisions',
      content: [
        'The **Start Cooking CTA** was made more prominent and given clearer iconography so users didn\'t have to hunt for it. The **step-by-step cooking mode** was consolidated into a single flow, with ingredients and utensils shown per step so you\'re never leaving the screen to cross-reference.',
        '**Ingredients and utensils** moved from run-on horizontal paragraphs to a scannable vertical list. The **content order** was restructured so ingredients and utensils appear near the top — where users actually needed them — and reviews and tags moved to the bottom, where they naturally expected to find them.',
        'The **cook timer** changed from orange text with a clock emoji to a distinct, tappable button. Most users in Round 1 never realized the former even launched a built-in timer. A single visual affordance change fixed that entirely.',
        '**Premium features** got quiet "Plus" labels so the paywall was never an unpleasant surprise.'
      ]
    },
    {
      type: 'image-single',
      image: { src: '/images/kitchen-stories/cta-evolution.png', alt: 'Start Cooking CTA evolution', caption: 'Start Cooking CTA evolution' }
    },
    {
      type: 'image-grid',
      columns: 3,
      images: [
        { src: '/images/kitchen-stories/before-1.png', alt: 'Start Cooking button', caption: 'Start Cooking! button' },
        { src: '/images/kitchen-stories/before-2.png', alt: 'Obscure timer feature', caption: 'Obscure timer feature' },
        { src: '/images/kitchen-stories/before-3.png', alt: 'Hidden Plus features', caption: 'Hidden Plus features' }
      ]
    },
    {
      type: 'image-grid',
      columns: 3,
      images: [
        { src: '/images/kitchen-stories/after-1.png', alt: 'Start Cooking button redesigned', caption: 'Start Cooking button' },
        { src: '/images/kitchen-stories/after-2.png', alt: 'Visible timer feature', caption: 'Visible timer feature' },
        { src: '/images/kitchen-stories/after-3.png', alt: 'Visible Plus features', caption: 'Visible Plus features' }
      ]
    },
    {
      type: 'two-col-text',
      heading: 'Results',
      content: [
        'Round 2 testing confirmed the changes worked. Task time from recipe discovery to starting cooking dropped from 2:43 to 1:32 — 44% faster. Immediate Start Cooking discovery jumped from 60% to 95%. UI clarity and ease of navigation both climbed nearly a full point on a 5-point scale.'
      ]
    },
    {
      type: 'callout',
      text: 'Every participant preferred the redesign — and none of them needed to be told why.'
    },
    {
      type: 'two-col-text',
      heading: 'Reflection',
      content: [
        "This project made a strong case for targeted restraint. The biggest gains came from changes that didn't touch the visual identity at all — just structural decisions about where things lived and how they were labeled.",
        'The hardest part was resisting the urge to redesign more. Keeping the scope tight meant every change could be traced directly back to observed user behavior, which made the outcome both cleaner and easier to defend.'
      ]
    },
    {
      type: 'image-single',
      image: { src: '/images/kitchen-stories/final.png', alt: 'Proposed redesign overview', caption: 'Proposed redesign overview' }
    }
  ]
};
